import { IProject } from '@api/models/project.model';
import { IUser } from '@api/models/user.model';
import { Switch } from '@library/forms';
import { List, ListItem } from '@library/list';
// import { CircularProgress } from '@library/loaders/components';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { produce } from 'immer';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import useProjectHierarchySelection from 'modules/shared/hooks/use-project-hierarchy-selection';
import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, CircularProgress, Drawer, LinearProgress, NavTab } from '@hyperflake/react-ui-library';
import useProjectAccessForm from 'modules/users/hooks/useProjectAccessForm';
import { PermissionEnum, ProjectAssetEnum } from '@api/enum';
import ExternalProjectList from '../ExternalProjectList/ExternalProjectList';
import { Accordian, AccordianBody, AccordianCollapse, AccordianHeader, AccordianIcon } from '@library/accordian';
import OverflowTooltip from '@components/OverflowTooltip/OverflowTooltip';

interface IProjectAccessListFormProps {}

interface projectAccessForm {
    projects: IProject[];
    selectedIds: string[];
    assets?: string[];
}

enum TabEnum {
    NORMAL_PROJECTS,
    EXTERNAL_PROJECTS,
}

const ALL_ASSETS = new Set(Object.values(ProjectAssetEnum));

const toAssetEnum = (val: unknown): ProjectAssetEnum | null => {
    if (typeof val !== 'string') return null;
    const normalized = val.trim().toUpperCase().replace(/\s+/g, '_'); // handle " TIMELAPSE" etc
    return ALL_ASSETS.has(normalized as ProjectAssetEnum) ? (normalized as ProjectAssetEnum) : null;
};

const getAvailableAssetsForProject = (p?: Pick<IProject, 'assets'>): ProjectAssetEnum[] => {
    if (!p?.assets?.length) return [];

    const names = p.assets
        .map((a) => a?.name) // take the enum-ish name
        .filter((s): s is string => typeof s === 'string' && s.length > 0);

    const enums = names
        .map(toAssetEnum) // normalize & validate against enum
        .filter((x): x is ProjectAssetEnum => !!x);

    // de-duplicate while preserving order
    return Array.from(new Set(enums));
};

const intersect = (a: ProjectAssetEnum[], b: ProjectAssetEnum[]) => {
    const setB = new Set(b);
    return Array.from(new Set(a.filter((x) => setB.has(x))));
};

const ProjectAccessListForm = (props: IProjectAccessListFormProps) => {
    // const { userId, onClose, open } = props;
    const { clientId, userId } = useParams();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState(TabEnum.NORMAL_PROJECTS);

    const { data, isLoading, user, userLoading, submit, isSubmitting } = useProjectAccessForm(clientId, userId);

    const projects = useMemo(() => {
        if (!data) return [];
        return data;
    }, [data]);

    const { hasPermission } = useAuthPermission();

    interface ProjectAccessForm {
        selectedIds: string[];
        projectAssetPermissions: Record<string, ProjectAssetEnum[]>;
    }

    const initialValues: ProjectAccessForm = useMemo(() => {
        const selectedIds = user?.projects?.map((p) => (p as IProject)._id).filter(Boolean) ?? [];
        const fromUser = (user?.projectAssetPermissions ?? {}) as Record<string, string[]>;

        const map: Record<string, ProjectAssetEnum[]> = {};
        (projects ?? []).forEach((p) => {
            const available = getAvailableAssetsForProject(p);
            const userPicked = (fromUser[p._id] ?? []).map(toAssetEnum).filter(Boolean) as ProjectAssetEnum[];
            map[p._id] = intersect(userPicked, available); // only keep assets allowed by the project
        });

        return { selectedIds, projectAssetPermissions: map };
    }, [user, projects]);

    const onUserUpdate = (data: IUser) => {
        queryClient.setQueryData<IUser[]>(['clients', clientId, 'users'], (tempData) => {
            if (!tempData) return;

            const index = tempData.findIndex((user) => userId === user._id);
            if (index < 0) return tempData;

            return produce(tempData, (draft) => {
                draft[index] = data;
                return draft;
            });
        });
    };

    const handleSubmit = async (projectList: any) => {
        const structureData = {
            ...projectList,
            projects: projectList.selectedIds,
        };
        try {
            const data = await submit(structureData);
            toast.success(`${user?.name}'s project access has been updated.`);
            onUserUpdate(data);
            // onClose();
        } catch (e: any) {
            console.log(e);
            toast.error('some thing went wrong');
        }
    };

    return data?.length === 0 ? (
        <div>
            <>
                <div className="">
                    <h4>Project Access</h4>
                    <div className="mt-4 text-base font-semibold">
                        Manage {user ? user.name : userId}'s project access.
                    </div>
                </div>
                <div className="flex flex-col justify-center items-center h-full">
                    <div className="text-lg">This client has no project at the moment.</div>
                </div>
            </>
        </div>
    ) : (
        <div>
            <div>
                <div className="mt-4 text-base font-semibold">Manage {user ? user.name : userId}'s project access.</div>
            </div>
            <div className="">
                <Formik<ProjectAccessForm>
                    initialValues={initialValues}
                    enableReinitialize
                    validateOnChange={false}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue }) => (
                        <Form>
                            <fieldset disabled={!hasPermission(PermissionEnum.USER_UPDATE)}>
                                <div className="mt-4 h-72 overflow-y-auto">
                                    <ProjectList
                                        projects={projects}
                                        selectedIds={values.selectedIds}
                                        assetPermissions={values.projectAssetPermissions}
                                        onSelectedIdsChange={(updatedIds) => {
                                            const removed = values.selectedIds.filter((id) => !updatedIds.includes(id));
                                            const nextPerms = { ...values.projectAssetPermissions };

                                            // When a project is deselected, clear all its asset permissions
                                            removed.forEach((projectId) => {
                                                nextPerms[projectId] = []; // Remove assets when project is deselected
                                            });

                                            // Enable all assets for selected projects
                                            updatedIds.forEach((projectId) => {
                                                if (nextPerms[projectId]) {
                                                    const availableAssets = getAvailableAssetsForProject(
                                                        projects.find((p) => p._id === projectId)
                                                    );
                                                    nextPerms[projectId] = Array.from(new Set(availableAssets)); // Enabling all assets for the project
                                                }
                                            });

                                            setFieldValue('selectedIds', updatedIds);
                                            setFieldValue('projectAssetPermissions', nextPerms);
                                        }}
                                        onAssetToggle={(projectId, asset, enabled) => {
                                            const current = values.projectAssetPermissions[projectId] ?? [];
                                            const next = enabled
                                                ? Array.from(new Set([...current, asset]))
                                                : current.filter((a) => a !== asset);

                                            // If the project is deselected, clear all assets for that project
                                            if (!values.selectedIds.includes(projectId)) {
                                                setFieldValue(`projectAssetPermissions.${projectId}`, []); // Deselect all assets when project is disabled
                                            } else {
                                                setFieldValue(`projectAssetPermissions.${projectId}`, next);
                                            }
                                        }}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    size="lg"
                                    className="mt-6 w-full sm:w-auto"
                                >
                                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                                </Button>
                            </fieldset>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};
interface IProjectListProps {
    projects: IProject[];
    selectedIds: string[];
    assetPermissions: Record<string, ProjectAssetEnum[]>;
    onSelectedIdsChange: (projectIds: string[]) => void;
    onAssetToggle: (projectId: string, asset: ProjectAssetEnum, enabled: boolean) => void;
}

const ProjectList = ({
    projects,
    selectedIds,
    assetPermissions,
    onSelectedIdsChange,
    onAssetToggle,
}: IProjectListProps) => {
    const projectHierarchy = useProjectHierarchySelection({ projects, selectedIds, onSelectedIdsChange });

    const flatten = () => {
        const out: IProject[] = [];
        const dig = (id: string) => {
            if (!projectHierarchy.hasChildren(id)) return;
            projectHierarchy.getChildren(id).forEach((p) => {
                out.push(p);
                dig(p._id);
            });
        };
        (projectHierarchy.getChildren('root') ?? []).forEach((p) => {
            out.push(p);
            dig(p._id);
        });
        return out;
    };

    const projectList = flatten();

    return (
        <List divider>
            {projectList.map((row) => {
                const isSelected = projectHierarchy.isSelected(row._id);
                const availableAssets = getAvailableAssetsForProject(row);
                const enabledAssets = assetPermissions[row._id] ?? [];

                return (
                    <div key={row._id} style={{ marginLeft: `${row.hierarchyLevel * 10}px` }}>
                        <Accordian defaultExpanded className="mb-2">
                            <AccordianHeader className="w-full" showIcon={false}>
                                <div className="flex flex-col py-2">
                                    <div className="text-xs text-gray-500">{row._id}</div>
                                    <div className="text-base">{row.name}</div>
                                </div>

                                <div className="flex gap-8">
                                    <div className="flex items-center gap-2">
                                        <div className="text-gray-400 text-xs">Assets</div>
                                        <AccordianIcon />
                                    </div>
                                    <Switch
                                        onClick={(e) => e.stopPropagation()}
                                        checked={isSelected}
                                        onChange={(e) => projectHierarchy.changeSelected(row, e.target.checked)}
                                    />
                                </div>
                            </AccordianHeader>

                            <AccordianCollapse>
                                <AccordianBody className="p-4 bg-gray-100 rounded-lg">
                                    <div className="text-gray-400 text-xs font-semibold">Project Assets</div>

                                    {availableAssets.length === 0 ? (
                                        <div className="mt-2 text-xs text-gray-500">
                                            No assets configured for this project.
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2 mt-2">
                                            {availableAssets.map((asset) => {
                                                const checked = enabledAssets.includes(asset);
                                                return (
                                                    <div
                                                        key={`${row._id}-${asset}`}
                                                        className="text-sm font-semibold flex items-center justify-between"
                                                    >
                                                        <label
                                                            htmlFor={`asset-${row._id}-${asset}`}
                                                            className="flex items-center gap-2 select-none overflow-hidden flex-1 cursor-pointer text-sm"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <OverflowTooltip className="font-semibold text-xs">
                                                                {asset.replace(/_/g, ' ')}
                                                            </OverflowTooltip>
                                                        </label>

                                                        <Switch
                                                            id={`asset-${row._id}-${asset}`}
                                                            checked={checked}
                                                            size="sm"
                                                            disabled={!isSelected} // cannot toggle if project is off
                                                            onChange={(e) =>
                                                                onAssetToggle(row._id, asset, e.target.checked)
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </AccordianBody>
                            </AccordianCollapse>
                        </Accordian>
                    </div>
                );
            })}
        </List>
    );
};
export default ProjectAccessListForm;
